import React, {useState, useCallback, useEffect, useRef} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {Button} from '../../../shared/Button'

import {TabToggle} from '../../../shared/Tabs'
import {AccountRow} from '../components/AccountRow'
import {Input} from '../../../shared/Input'
import {Spinner} from '../../../../components/loader-spinner'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {fetchAccounts} from '../../../../app-store/accounts/accountsThunk'
import {resetAccounts} from '../../../../app-store/accounts/accountsSlice'
import {accountsApi} from '../../../../app-store/accounts/accountsApi'
import {
  goNext,
  setStep,
  setCountrySelectState,
  setSelectedCountryStates,
  selectCountrySelectState,
  setEmailLookupState,
} from '../../../../app-store/AccountSetupSlice'
import countryRegionJSON from '../../../../utils/country-region.json'
import {fetchSelectedCountryRegionList} from '../../AccountInfo/services/locationAPI'
import Drawer from '../../../shared/Drawer'
import AccountDetailsDrawer from '../components/AccountDetailsDrawer'
import SubscriptionDetailsDrawer from '../components/SubscriptionDrawer'
import Modal from '../../../shared/Modal'
import {Modal as PastDueInvoiceModal} from '@thryvlabs/maverick'
import {
  hasBlockingPastDueInvoice,
  SHOW_INVOICE_INFO_FOR_PAST_DUE,
} from '../../../../utils/past-due-invoice'
import {InvoiceInformation} from '../../../../components/account-information-components/invoice-information'
import {AccountEditModal} from '../components/AccounEditModal'
import {
  SchedulingLayout,
  LocationTabList,
  LocationTabCard,
  LocationTabRow,
  LocationTabMainIcon,
  LocationTabDetailsCol,
  LocationTabTopRow,
  LocationTabIndexLabel,
  LocationTabName,
  LocationTabBadge,
  LocationDetailStack,
} from '../../AccountSetup/components/SchedulingStep.styles'
import Icon from '../../../../core/shared/Icon'
import {ResolvedLocationSummary} from '../components/ResolvedLocationSummary'
import {
  formValuesToBillingPayload,
  applyValuesToAccount,
  locationToFormValues,
  buildXpCodeEditTarget,
  clearPartnerAttribution,
} from './AllAccountsFormMapping'
import {useAsync} from '../../../../utils/use-async'
import useDebounce from '../../../../utils/useDebounce'
import {useAddressValidation} from '../hooks/useAddressValidation'
import {useUserFeatures} from '../../../../context/user-features-context'
import {isRestrictedAccountCreationCountry} from '../../AccountInfo/AccountInfo'
import {
  ContentArea,
  Toolbar,
  SearchWrapper,
  SearchRow,
  AccountList,
  StatusMessage,
} from '../AccountsTab.styles'
import {
  StyledAddIcon,
  StyledSearchIcon,
} from '../../../shared/icons/icons.styles'
import {
  selectEffectiveRole,
  selectUserRoles,
} from '../../../../app-store/selector'
import {buildPurchaseLinkState} from '../purchaseLinkState'
import {resolveLocationAccountId} from '../locationIdentity'
import {
  setAccountInfo,
  initLocations,
  setProductConfigInfo,
  setRecurlyAccountInfoForLocation,
  setIsUpgrade,
  setSalesAttributionFields,
} from '../../../../app-store/PurchaseLinkSlice'
import {applyProductRulesBeforeLoading} from '../../../../features/CatalogGrid/utils/productPreloadRules'
import {getTaxIdentifier} from '../../AccountInfo/utils/taxIdentifier'
import {buildBehalfRepUpgradeFields} from '../../AccountInfo/utils/behalfRep'
import {
  getAccountManagementLandingPath,
  getAccountTabs,
  isAccountPathHiddenForRoles,
  shouldSkipOrderEditModal,
  shouldUseLegacyPartnerOpsFlow,
} from '../../../../utils/account-management-navigation'

// Icons

// function SearchIcon() {
//   return (
//     <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
//       <circle
//         cx="6.5"
//         cy="6.5"
//         r="4"
//         stroke="currentColor"
//         strokeWidth="2"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//       <path
//         d="M11 11l2.5 2.5"
//         stroke="currentColor"
//         strokeWidth="2"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   )
// }

const withAccountInfoXpCode = (
  accountSubscriptionData,
  xpCode,
  salesRepOrPartner,
) => {
  // PartnerOps accounts get accountInfo.xpCode auto-populated server-side from
  // the account's original keap_partner_links.keap_partner_id (see
  // validatePartnerOpsAccount / account-service.js's getAccountSubscriptions) —
  // with no client-side xpCode/salesRepOrPartner to pass in here at all. That
  // join can only ever produce a Keap Partner ID, so default salesRepOrPartner
  // to 'keap-partner' whenever accountInfo already carries an xpCode we didn't
  // just set ourselves. Bail out only when there's truly nothing to attach.
  const existingXpCode = accountSubscriptionData?.accountInfo?.xpCode
  const resolvedSalesRepOrPartner =
    salesRepOrPartner || (existingXpCode ? 'keap-partner' : undefined)
  if (!xpCode && !resolvedSalesRepOrPartner) return accountSubscriptionData
  return {
    ...(accountSubscriptionData || {}),
    accountInfo: {
      ...(accountSubscriptionData?.accountInfo || {}),
      ...(xpCode && {xpCode}),
      ...(resolvedSalesRepOrPartner && {
        salesRepOrPartner: resolvedSalesRepOrPartner,
      }),
    },
  }
}

const getLegacySubscriptionId = loc =>
  loc?.subscriptionId ||
  loc?.recurlySubscriptionId ||
  loc?.RECURLY_SUBSCRIPTION_ID ||
  loc?.uuid

// GEP-6957: country isn't editable in the edit modal, so every restricted-country
// check against an editingTarget (the render-time disabled state below, and the
// "Save & Proceed to Order" gate in handleSaveAndProceed) needs to read it off the
// same account/parentAccount fields the same way — including the `countryCode`
// fallback (some account shapes only carry it there, e.g. a partial/Keap-partner
// record) and the `selectedCountry` state that resolveEditCountry ultimately falls
// back to for the actual address-validation call. Without matching that fallback
// chain exactly, this check can read `undefined` (miss a restricted country) while
// runAddressValidation still resolves and submits the real country, which the
// backend's validateAddressSchema rejects with a raw Joi error instead of this
// modal's friendly message.
const getEditingTargetCountry = (target, selectedCountry) =>
  (target?.type === 'add-location'
    ? target?.parentAccount?.address?.country ||
      target?.parentAccount?.country ||
      target?.parentAccount?.countryCode
    : target?.account?.address?.country ||
      target?.account?.country ||
      target?.account?.countryCode) || selectedCountry

const buildLegacyUpgradeOrder = (loc, account, emailValidation = {}) => {
  const subscriptionId =
    getLegacySubscriptionId(loc) || emailValidation?.subscriptionId
  return {
    billingAccountId: subscriptionId,
    subscriptionId,
    thryvId:
      loc?.thryvId ||
      loc?.thryvID ||
      account?.thryvId ||
      loc?.account_id ||
      loc?.id ||
      loc?.code,
    v5upgrade: false,
    ...(emailValidation && {emailValidation}),
    accountSearchResult: {
      parent: account,
      location: loc,
    },
  }
}

const payBadgeStyle = {
  parent: {
    background: '#E6F1FB',
    color: '#0C447C',
    border: '0.5px solid #85B7EB',
  },
  self: {
    background: '#EAF3DE',
    color: '#27500A',
    border: '0.5px solid #97C459',
  },
}

const hasRole = (roles, targetRole) => {
  const roleList = Array.isArray(roles) ? roles : roles ? [roles] : []
  const target = String(targetRole).toLowerCase()
  return roleList.some(role => String(role).toLowerCase() === target)
}

// Minimum characters before a non-email lookup (Thryv ID / EAID / ORG-LST code)
// will auto-fire. Emails gate on structure instead of length (see isSearchReady).
const MIN_NON_EMAIL_SEARCH_LENGTH = 5

// Whether the current input is "complete enough" to auto-fire a backend lookup.
// Each fire is a real identity lookup, so unlike a client-side filter we hold off
// until the input is plausibly finished:
//   - email: must contain '@' followed by a '.' with at least one char on each
//            side (e.g. a@b.c) — avoids lookups on half-typed addresses.
//   - Thryv ID / EAID / account code: opaque strings with no completeness signal,
//            so gate on a minimum length and let the debounce catch the pause.
const isSearchReady = value => {
  const trimmed = (value || '').trim()
  if (!trimmed) return false
  if (trimmed.includes('@')) {
    const at = trimmed.indexOf('@')
    const dot = trimmed.indexOf('.', at)
    return dot > at + 1 && dot < trimmed.length - 1
  }
  return trimmed.length >= MIN_NON_EMAIL_SEARCH_LENGTH
}

export function AllAccounts({
  initialSearch = '',
  isEmbedded = false,
  onBack,
  onAddAccount,
  onOrder,
}) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const client = useAuthClient()

  const {Override_SST: frontendOverrideSst} = useUserFeatures()
  const {actualRole} = useSelector(selectUserRoles)
  const effectiveRoles = useSelector(selectEffectiveRole)
  const userFeatures = useUserFeatures()
  const {Enable_International_Account_Creation} = userFeatures
  const accountTabs = getAccountTabs(effectiveRoles, userFeatures)
  const accountManagementLandingPath =
    getAccountManagementLandingPath(effectiveRoles)

  const [orderingThryvId, setOrderingThryvId] = useState(null)
  const [restrictedCountryModalOpen, setRestrictedCountryModalOpen] =
    useState(false)
  const [showPastDueInvoiceModal, setShowPastDueInvoiceModal] = useState(false)
  const [pastDueInvoiceContext, setPastDueInvoiceContext] = useState(null)

  const location = useLocation()
  const state = location.state

  useEffect(() => {
    if (
      !isEmbedded &&
      isAccountPathHiddenForRoles(location.pathname, effectiveRoles)
    ) {
      navigate(accountManagementLandingPath, {replace: true})
    }
  }, [
    accountManagementLandingPath,
    effectiveRoles,
    isEmbedded,
    location.pathname,
    navigate,
  ])

  const {results, loading, error} = useSelector(
    state => state.accounts.accounts,
  )

  const {selectedCountry} = useSelector(selectCountrySelectState)

  const loadCountryStates = useCallback(
    country => {
      const resolved = country || 'US'
      dispatch(setCountrySelectState(resolved))
      dispatch(
        setSelectedCountryStates(
          fetchSelectedCountryRegionList(resolved, countryRegionJSON),
        ),
      )
    },
    [dispatch],
  )

  const [subCounts, setSubCounts] = useState({})
  const [subCache, setSubCache] = useState({})

  useEffect(() => {
    const allChildren = results
      .filter(({parent}) => parent != null)
      .flatMap(({children}) => children)
    setSubCounts({})
    setSubCache({})
    if (allChildren.length === 0) return
    const childIds = [
      ...new Set(
        allChildren
          .map(c => (c._fromBillingService ? c.id : c.code || c.id))
          .filter(Boolean),
      ),
    ]
    if (childIds.length === 0) return
    const counts = {}
    const cache = {}
    Promise.all(
      childIds.map(thryvId =>
        client(`accounts/5/subscriptions?thryvId=${thryvId}`)
          .then(data => {
            const EXCLUDED = ['app-websitebuilder', 'addon-5-signatures_usage']
            const subs = (data?.childSubscriptions ?? []).filter(
              sub => !EXCLUDED.includes(sub.code),
            )
            const n = subs.reduce(
              (acc, sub) =>
                acc +
                1 +
                (sub.addons?.filter(a => !EXCLUDED.includes(a.code)).length ??
                  0),
              0,
            )
            counts[thryvId] = n
            cache[thryvId] = data
          })
          .catch(() => {}),
      ),
    ).then(() => {
      setSubCounts(counts)
      setSubCache(cache)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results])

  const accounts = results
    .filter(({parent}) => parent != null)
    .map(({parent, children}) => ({
      ...parent,
      locations: (children || [])
        .filter(c => !c.isSandbox)
        .map(child => {
          const childId = child._fromBillingService
            ? child.id
            : child.code || child.id
          return {
            ...child,
            name: child.company || child.companyName || child.name,
            city: child.city || child.address?.city,
            stateCode:
              child.stateCode || child.address?.region || child.address?.state,
            subs: subCounts[childId] ?? child.subs ?? null,
          }
        }),
    }))

  const [searchQuery, setSearchQuery] = useState(initialSearch || '')
  const [hasSearched, setHasSearched] = useState(false)
  // Slightly longer than MyAccounts' 300ms — each fire hits external identity
  // lookups (Recurly/billing service), so give the user more time to finish.
  const debouncedSearch = useDebounce(searchQuery, 400)
  // A value that was already fetched before/at mount (initialSearch, emailLookup
  // prefetch). The auto-search effect skips it once so we don't issue a duplicate
  // lookup for the seeded query.
  const preloadedSearchRef = useRef(null)

  // True only when searching by email AND the email doesn't appear as any
  // parent's email — meaning the searched email only exists as a child location.
  // Thryv ID searches must never enable the New Account button.
  const emailToCheck = (isEmbedded ? initialSearch : searchQuery)
    .trim()
    .toLowerCase()
  const isSearchByEmail = emailToCheck.includes('@')
  const isEmailOnlyChild =
    isSearchByEmail &&
    hasSearched &&
    !loading &&
    accounts.length > 0 &&
    !accounts.some(acc => (acc.email || '').toLowerCase() === emailToCheck)
  // True when searching by email and no accounts exist at all — the email is
  // brand new and available, so "New Account" should be enabled.
  const isEmailNotFound =
    isSearchByEmail && hasSearched && !loading && accounts.length === 0

  // Drawer state
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedLoc, setSelectedLoc] = useState(null)

  /**
   * editingTarget shape:
   *   { account, accountId, type: 'parent' | 'child' | 'add-location', parentAccount? }
   */
  const [editingTarget, setEditingTarget] = useState(null)
  const [editValues, setEditValues] = useState({})
  const [saving, setSaving] = useState(false)
  const {run} = useAsync({status: 'idle'})
  const {validateAddress, isValidatingAddress} = useAddressValidation(
    client,
    run,
  )
  const [addressErrors, setAddressErrors] = useState({})
  // Set when Google's Address Validation API returns a plausible-but-not-
  // confirmed ("SUGGESTED") correction for the representative location's
  // address — rendered as an inline entered-vs-recommended banner inside
  // AccountEditModal instead of a separate confirm dialog.
  const [addressSuggestion, setAddressSuggestion] = useState(null)
  // Forces the hidden location/identity fields back into view when the
  // "Create Purchase Link" order-flow modal (hideLocationDetails) fails
  // required-field validation, so the rep can actually see and fix the
  // field the toast just told them about.
  const [revealLocationDetails, setRevealLocationDetails] = useState(false)
  // Non-null while reviewing a multi-location ("parentPay") purchase link
  // whose address validation flagged one or more locations — see
  // validateLocationsBatch/handleSaveReviewLocation below.
  const [multiLocationReview, setMultiLocationReview] = useState(null)
  // Set right before validation runs in handleSaveAndProceed vs handleSaveInfo
  // so that once a suggestion banner appears, handleConfirmSuggestion still
  // knows which button the rep originally clicked — otherwise "Use recommended"
  // always falls back to save+close even when the rep meant to proceed to order.
  const proceedAfterSuggestionRef = useRef(false)

  const resolveEditCountry = useCallback(() => {
    return editingTarget?.account?.address?.country || selectedCountry
  }, [editValues, editingTarget, selectedCountry])

  const runAddressValidation = useCallback(async () => {
    setAddressErrors({})
    setAddressSuggestion(null)
    const result = await validateAddress({
      ...editValues,
      country: resolveEditCountry(),
    })
    if (!result.success && result.fieldErrors) {
      setAddressErrors(result.fieldErrors)
      if (Object.keys(result.fieldErrors).length > 0) {
        setRevealLocationDetails(true)
      }
    }
    if (!result.success && result.suggestion) {
      setAddressSuggestion(result.suggestion)
      setRevealLocationDetails(true)
    }
    if (result.success && result.values) {
      setEditValues(prev => ({...prev, ...result.values}))
    }
    return result
  }, [validateAddress, editValues, resolveEditCountry])

  const validateLocationsBatch = useCallback(
    async locations =>
      Promise.all(
        locations.map(async loc => {
          const locationId = resolveLocationAccountId(loc)
          let fresh = loc
          try {
            // The account-details response does not include _fromBillingService.
            // Keep the source row's identity metadata so the later parent-pay
            // review still resolves a billing-service child by its unique id,
            // rather than its shared EAID in code.
            fresh = {
              ...loc,
              ...(await accountsApi.getAccountById(client, locationId)),
            }
          } catch {
            // Fall back to the cached row if the fresh fetch fails — still
            // attempt validation against whatever address data we have.
          }
          const values = locationToFormValues(fresh)
          const result = await validateAddress({
            ...values,
            country: fresh?.address?.country || fresh?.country || loc?.country,
          })
          return {
            locationId,
            account: fresh,
            values: result.values || values,
            status: result.success ? 'verified' : 'needs-attention',
            fieldErrors: result.fieldErrors || {},
            suggestion: result.suggestion || null,
          }
        }),
      ),
    [client, validateAddress],
  )

  const setActiveReviewLocation = useCallback(locationId => {
    setMultiLocationReview(prev =>
      prev ? {...prev, activeLocationId: locationId} : prev,
    )
  }, [])

  const updateReviewLocationValues = useCallback((locationId, values) => {
    setMultiLocationReview(prev =>
      prev
        ? {
            ...prev,
            valuesByLocation: {...prev.valuesByLocation, [locationId]: values},
          }
        : prev,
    )
  }, [])

  useEffect(() => {
    if (results.length > 0) setHasSearched(true)
    if (initialSearch) {
      // searchQuery is already seeded to initialSearch — fetch it once and let
      // the auto-search effect skip this same value.
      preloadedSearchRef.current = initialSearch
      runSearch(initialSearch, {explicit: true})
    } else if (state?.context === 'emailLookupReady') {
      // Results were pre-fetched before navigation — just populate the search
      // box and mark as searched; no second API call needed.
      preloadedSearchRef.current = state.email
      setSearchQuery(state.email)
      setHasSearched(true)
    } else if (state?.context === 'emailLookup') {
      preloadedSearchRef.current = state?.email
      setSearchQuery(state?.email)
      runSearch(state?.email, {explicit: true})
    } else if (state?.context === 'returnFromAccountSetup' && state?.email) {
      // Cancel from the account-setup wizard's Catalog Selection step (or
      // anywhere else in it) restores whatever search the rep had before
      // clicking "Create Purchase Link"/"New Account" — see handleCancel in
      // AccountSetup/index.js.
      preloadedSearchRef.current = state.email
      setSearchQuery(state.email)
      runSearch(state.email, {explicit: true})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      dispatch(resetAccounts())
    }
  }, [dispatch])

  // Search

  const handleSearchChange = useCallback(
    e => {
      setSearchQuery(e.target.value)
      setHasSearched(false)
      dispatch(resetAccounts())
    },
    [dispatch],
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setHasSearched(false)
    dispatch(resetAccounts())
  }, [dispatch])

  // Core lookup. `explicit` is true for Enter/click and the post-save refetch —
  // those surface the @thryv.com error toast. Auto-search (typing) passes
  // explicit:false and silently ignores disallowed input instead of toasting.
  const runSearch = useCallback(
    (rawValue, {explicit = false} = {}) => {
      const value = (rawValue ?? '').trim()

      if (!value) return

      const isEmail = value.includes('@')

      if (isEmail && value.toLowerCase().includes('@thryv.com')) {
        if (explicit) {
          toast.error('Thryv addresses are not allowed')
          setHasSearched(false)
          dispatch(resetAccounts())
        }
        return
      }

      // Non-email input may be a Thryv ID, an ORG*/LST* account code, or an EAID
      // (Recurly account username) — the backend tries each lookup in turn.
      setHasSearched(true)
      dispatch(
        fetchAccounts({
          client,
          email: isEmail ? value : undefined,
          thryvId: !isEmail ? value : undefined,
        }),
      )
    },
    [dispatch, client],
  )

  // Explicit trigger: Enter key, and the post-save refetch in handleSaveInfo /
  // handleSaveAndProceed (called with no argument → uses the current query).
  const handleSearch = useCallback(
    input => {
      const raw =
        typeof input === 'string' ? input : input?.target?.value ?? searchQuery
      runSearch(raw, {explicit: true})
    },
    [runSearch, searchQuery],
  )

  // Auto-search: fire the lookup once the input looks complete and the user
  // pauses (debounced). No magnifying-glass click required.
  useEffect(() => {
    if (!isSearchReady(debouncedSearch)) return
    // Skip the seeded/prefetched value exactly once so we don't duplicate the
    // lookup the mount effect already issued for it.
    if (debouncedSearch.trim() === (preloadedSearchRef.current || '').trim()) {
      preloadedSearchRef.current = null
      return
    }
    runSearch(debouncedSearch, {explicit: false})
  }, [debouncedSearch, runSearch])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') handleSearch()
    },
    [handleSearch],
  )

  const handleNewAccount = useCallback(() => {
    if ((isEmailOnlyChild || isEmailNotFound) && searchQuery) {
      const email = searchQuery.trim()
      dispatch(setEmailLookupState({email, accountType: null, meta: null}))
      dispatch(goNext())
    }
    navigate('/accounts/account-setup', {
      state: {returnTo: '/accounts/all-accounts', returnSearch: searchQuery},
    })
  }, [navigate, dispatch, isEmailOnlyChild, isEmailNotFound, searchQuery])

  // Order

  const navigateToUpgrade = useCallback(
    order => {
      navigate('/order/upgrade', {
        state: {order, navFrom: 'accounts', currentStep: 1},
      })
    },
    [navigate],
  )

  const openPartnerOpsXpCodeEdit = useCallback(
    async (cachedAccount, orderContext) => {
      const accountId = cachedAccount?._fromBillingService
        ? cachedAccount?.id
        : cachedAccount?.code || cachedAccount?.thryvId || cachedAccount?.id
      const cachedCountry =
        cachedAccount?.address?.country ||
        cachedAccount?.country ||
        cachedAccount?.countryCode ||
        'US'

      setOrderingThryvId(null)
      loadCountryStates(cachedCountry)
      setEditValues({})
      // PartnerOps always sees the XP / Affiliate Code step within the Create Purchase
      // Link flow, so mark it fromOrder: the modal hides the location identity/address +
      // Bill To / Business Phone block and shows only the sales-attribution fields, exactly
      // like the normal Create Purchase Link modal. The code starts empty (re-cleared after
      // the fetch below, which would otherwise bring back the account's stored value).
      setEditingTarget(
        buildXpCodeEditTarget(
          clearPartnerAttribution(cachedAccount),
          accountId,
          orderContext,
        ),
      )

      if (!accountId) return

      try {
        const fresh = await accountsApi.getAccountById(client, accountId)
        setEditingTarget(prev =>
          prev?.accountId === accountId
            ? {
                ...prev,
                account: clearPartnerAttribution({...cachedAccount, ...fresh}),
                loading: false,
              }
            : prev,
        )
        if (fresh?.country || fresh?.address?.country) {
          loadCountryStates(fresh.country || fresh.address?.country)
        }
      } catch {
        setEditingTarget(prev =>
          prev?.accountId === accountId ? {...prev, loading: false} : prev,
        )
      }
    },
    [client, loadCountryStates],
  )

  const requirePartnerOpsXpCode = useCallback(
    (loc, orderContext) => {
      if (!hasRole(actualRole, 'PartnerOps')) return false
      // Always ask, whatever the account already carries: the rep types the XP /
      // Affiliate Code (and picks Keap / Legacy Partner) on every Create Purchase Link.
      openPartnerOpsXpCodeEdit(loc, orderContext)
      return true
    },
    [actualRole, openPartnerOpsXpCodeEdit],
  )

  // Edit / Add

  // Opens modal immediately with cached data, then replaces with fresh billing-service data.
  // Mirrors the same pattern used by MyAccounts openEditModal.
  const openEditModal = useCallback(
    async (
      cachedAccount,
      type,
      {
        fromOrder = false,
        orderMode,
        locationsOverride,
        parentAccountForOrder,
      } = {},
    ) => {
      // getAccounts (AllAccounts) returns billing-service objects with `code` = EAID (same
      // field getRepAccounts calls `thryvId`). thryvId is undefined on these objects.
      const accountId = cachedAccount?._fromBillingService
        ? cachedAccount?.id
        : cachedAccount?.code || cachedAccount?.thryvId || cachedAccount?.id
      const cachedCountry =
        cachedAccount?.address?.country ||
        cachedAccount?.country ||
        cachedAccount?.countryCode ||
        'US'
      // GEP-6957: block plain "Edit" too, not just "Create Purchase Link" —
      // Save Information is permanently disabled for a restricted-country
      // account regardless of how this modal was reached (see
      // isEditCountryRestricted below), so opening it can never lead anywhere
      // productive. Show the Client Care modal immediately instead of a form
      // where every button is a dead end. Redundant-safe for the order-flow
      // callers (handleOrder/handleOrderForParent already checked this before
      // calling here) — this is what actually gates the plain-Edit entry
      // point, which never goes through handleOrder at all.
      if (
        isRestrictedAccountCreationCountry(
          cachedCountry,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }
      loadCountryStates(cachedCountry)
      setEditValues({})
      setRevealLocationDetails(false)
      setAddressSuggestion(null)
      setMultiLocationReview(null)
      setEditingTarget({
        account: cachedAccount,
        type,
        accountId,
        loading: true,
        fromOrder,
        // Captured from the cached row now because the fresh getAccountById result
        // below (billing-service shape) does not carry isPre5.
        isPre5: !!cachedAccount?.isPre5,
        // handleOrder always sets orderMode + parentAccountForOrder (the true
        // parent it had in scope) here, since `account` above is just the
        // single clicked location, not the parent — handleSaveAndProceed
        // needs both to rebuild the purchase link against the real parent.
        // locationsOverride is only set for the account-header "Create
        // Purchase Link" flow (see handleOrderForParent), which combines
        // multiple parent-pay children into one order.
        orderMode,
        locationsOverride,
        parentAccountForOrder,
      })
      try {
        const fresh = await accountsApi.getAccountById(client, accountId)
        setEditingTarget(prev =>
          prev?.accountId === accountId
            ? {...prev, account: {...cachedAccount, ...fresh}, loading: false}
            : prev,
        )
        if (fresh?.country || fresh?.address?.country) {
          loadCountryStates(fresh.country || fresh.address?.country)
        }
      } catch {
        setEditingTarget(prev =>
          prev?.accountId === accountId ? {...prev, loading: false} : prev,
        )
      }
    },
    [client, loadCountryStates, Enable_International_Account_Creation],
  )

  const handleEditParent = useCallback(
    (account, options) => openEditModal(account, 'parent', options),
    [openEditModal],
  )

  const handleEditChild = useCallback(
    (loc, options) => openEditModal(loc, 'child', options),
    [openEditModal],
  )

  const handleOrder = useCallback(
    async (loc, account, {locationsOverride, orderMode = 'location'} = {}) => {
      // GEP-6957: block "Create Purchase Link" for accounts already in an
      // unsupported (non-prod) country while international account creation
      // is off — same flag/helper AccountInfo.js uses to hide unsupported
      // countries from the new-account dropdown. This must run before the
      // edit-modal branch below so restricted accounts never reach the order
      // flow, regardless of role.
      const orderCountry =
        loc?.country ||
        loc?.address?.country ||
        loc?.countryCode ||
        account?.country ||
        account?.address?.country ||
        account?.countryCode
      if (
        isRestrictedAccountCreationCountry(
          orderCountry,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }

      // Billing-service accounts use .id as the Recurly account code (e.g. LOC105F2495F).
      // Recurly-supplement accounts use .code as the account code (e.g. LOC21979B7AF) while
      // .id is the Recurly UUID — passing the UUID to code-{id} lookups returns not_found.
      const locThryvId = loc?._fromBillingService
        ? loc?.id
        : loc?.code || loc?.id
      // locThryvId (this specific clicked location's own resolved id) must win
      // over account?.thryvId — for a parent-pay order, `account` is the whole
      // hierarchy container, and its `.thryvId`-shaped field can be the
      // account's EAID rather than `loc`'s real thryvId, which 404s every
      // downstream accounts/5/subscriptions and accounts/upgradeTarget call.
      const thryvId =
        loc?.thryvId || loc?.thryvID || locThryvId || account?.thryvId

      if (!thryvId) {
        toast.error('Unable to start an upgrade without a Thryv ID.')
        return
      }

      // Checked here, before the edit-modal branch below, so "Create Purchase
      // Link" blocks up front instead of opening the edit modal first — the
      // past-due modal previously showed underneath that still-open modal.
      if (await hasBlockingPastDueInvoice(client, thryvId, orderCountry)) {
        setPastDueInvoiceContext({
          thryvId,
          country: orderCountry,
          email:
            loc?.email ||
            loc?.accountEmail ||
            account?.email ||
            account?.accountEmail,
        })
        setShowPastDueInvoiceModal(true)
        return
      }

      // Reps outside SKIP_ORDER_EDIT_MODAL_ROLES review/complete the location's (or, for a
      // pre-5 account, the account's) details in the Edit modal before a purchase link is
      // generated — "Save & Proceed to Order" in that modal runs the same navigation below.
      if (!shouldSkipOrderEditModal(effectiveRoles)) {
        if (account?.isPre5) {
          handleEditParent(account, {fromOrder: true})
        } else {
          handleEditChild(loc, {
            fromOrder: true,
            orderMode,
            parentAccountForOrder: account,
            ...(locationsOverride && {locationsOverride}),
          })
        }
        return
      }

      // A location that already carries a subscription id must still be
      // checked for v5 — otherwise v5 accounts with subscriptions skip the
      // shared Purchase Link wizard and land on the legacy /order/upgrade
      // catalog. If that check fails, keep the legacy flow.
      const legacySubscriptionId = getLegacySubscriptionId(loc)

      setOrderingThryvId(thryvId)
      try {
        const target = await client(
          `accounts/upgradeTarget?thryvId=${thryvId}`,
          {method: 'GET'},
        ).catch(err => {
          if (legacySubscriptionId) return null
          throw err
        })

        if (target?.v5upgrade) {
          const [accountSubscriptionsResponse, all5PlanPrices] =
            await Promise.all([
              client(`accounts/5/subscriptions?thryvId=${thryvId}`),
              client('plans/5/v1/list').catch(() => null),
            ])
          if (
            requirePartnerOpsXpCode(loc, {
              orderMode,
              locationsOverride,
              parentAccountForOrder: account,
            })
          ) {
            return
          }
          const accountSubscriptions = withAccountInfoXpCode(
            accountSubscriptionsResponse,
            loc?.xpCode || account?.xpCode,
            loc?.salesRepOrPartner || account?.salesRepOrPartner,
          )
          if (shouldUseLegacyPartnerOpsFlow(effectiveRoles)) {
            navigate('/order/upgrade', {
              state: {
                order: {
                  thryvId,
                  v5upgrade: true,
                  billTo: accountSubscriptions?.accountInfo?.billTo,
                  CCAccountInfo: {thryvId},
                  accountSubscriptionData: accountSubscriptions,
                },
                navFrom: 'accounts',
                currentStep: 1,
                prefetchedPlanPrices: all5PlanPrices,
                prefetchedAccountSubscriptions: accountSubscriptions,
              },
            })
            return
          }
          const purchaseLink = buildPurchaseLinkState({
            parentAccount: account,
            locationsOverride,
            accountSubscriptions,
            selectedLocation: loc,
            orderMode,
          })
          // Reuses Account Setup's Catalog Selection step (step 3) instead of
          // a dedicated route — jump straight there with PurchaseLinkSlice
          // pre-seeded, same as handleSingleLocNext does for a brand-new
          // account (see AccountSetup/index.js).
          dispatch(
            setProductConfigInfo(
              applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
            ),
          )
          dispatch(setAccountInfo(purchaseLink.accountInfo))
          dispatch(
            setSalesAttributionFields(purchaseLink.salesAttributionFields),
          )
          dispatch(
            initLocations({
              locations: purchaseLink.locations,
              selectedLocationId: purchaseLink.selectedLocationId,
              orderMode,
            }),
          )
          dispatch(setIsUpgrade(true))
          dispatch(setStep(2))
          navigate('/accounts/account-setup', {
            state: {
              returnTo: '/accounts/all-accounts',
              returnSearch: searchQuery,
            },
          })
          return
        }

        if (legacySubscriptionId || target?.subscriptionId) {
          navigateToUpgrade(
            buildLegacyUpgradeOrder(loc, account, {
              subscriptionId: target?.subscriptionId,
            }),
          )
          return
        }

        toast.error('Unable to find a subscription for this account.')
        setOrderingThryvId(null)
      } catch (err) {
        toast.error(err?.message || 'Unable to start the upgrade order.')
        setOrderingThryvId(null)
      }
    },
    [
      client,
      dispatch,
      navigate,
      navigateToUpgrade,
      requirePartnerOpsXpCode,
      effectiveRoles,
      handleEditParent,
      handleEditChild,
      Enable_International_Account_Creation,
      userFeatures,
      searchQuery,
    ],
  )

  // Header-level "Create Purchase Link" for accounts with more than one
  // parent-pay child — seeds the same flow as handleOrder but scoped to only
  // the parent-pay children (self-pay siblings are excluded from the
  // dropdown/order summary), flagged via orderMode so the catalog step
  // renders ParentPayLocationSelector + the combined multi-location summary.
  // A single (or no) parent-pay child has nothing to combine, so
  // AccountRow's hasParentPayChild already hides the button in that case;
  // this guard is just defensive parity.
  const handleOrderForParent = useCallback(
    account => {
      const parentPayLocations = (account.locations ?? []).filter(
        l => (l?.billTo ?? 'parent') !== 'self',
      )
      if (parentPayLocations.length <= 1) return
      handleOrder(parentPayLocations[0], account, {
        locationsOverride: parentPayLocations,
        orderMode: 'parentPay',
      })
    },
    [handleOrder],
  )

  // Opens a blank location form scoped to the parent account
  const handleAddLocation = useCallback(
    parentAccount => {
      const country =
        parentAccount?.address?.country ||
        parentAccount?.country ||
        parentAccount?.countryCode ||
        'US'
      // GEP-6957: same restricted-country gate as handleOrder — a parent
      // already in an unsupported country shouldn't get as far as the
      // add-location form (its "Save & Proceed to Order" is caught too, but
      // blocking here avoids the pointless intermediate form fill-out).
      if (
        isRestrictedAccountCreationCountry(
          country,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }
      loadCountryStates(country)
      setEditValues({})
      setEditingTarget({account: null, type: 'add-location', parentAccount})
    },
    [loadCountryStates, Enable_International_Account_Creation],
  )

  const saveAccount = useCallback(
    async ({manageSaving = true, resolvedValues, timeZone} = {}) => {
      const {accountId, account, type, parentAccount} = editingTarget || {}
      if (type === 'add-location') {
        if (manageSaving) setSaving(true)
        try {
          // Prefer the just-resolved address/timezone passed in explicitly over
          // `editValues` state — runAddressValidation's setEditValues hasn't been
          // through a re-render yet when saveAccount runs immediately after it in
          // the same handler, so reading state here would silently use the
          // pre-validation address and fall back to a hardcoded timezone.
          const values = resolvedValues || editValues
          const country =
            parentAccount?.country ||
            parentAccount?.address?.country ||
            selectedCountry ||
            'US'
          const billToRaw = values.billTo || 'parent'
          const payload = {
            parentCcId:
              parentAccount?.accountCode ||
              parentAccount?.id ||
              parentAccount?.code ||
              null,
            parentOrgId: parentAccount?.parentOrgId || null,
            email:
              values.email ||
              parentAccount?.accountEmail ||
              parentAccount?.email ||
              null,
            firstName: values.firstName,
            lastName: values.lastName,
            company: values.businessName || null,
            phone: values.mobile || values.telephone || null,
            country,
            timeZone: timeZone || 'America/Chicago',
            address: {
              street: values.address || null,
              city: values.city || null,
              state: values.state || null,
              zipCode: values.postCode || null,
              country,
            },
            abn: getTaxIdentifier(values) || null,
            sfdcContactEmail: values.salesforceEmail || null,
            billTo: billToRaw === 'self' ? 'child' : billToRaw,
            locationName: values.locationName || null,
          }
          const response = await client('multi-location/location', {
            method: 'POST',
            data: payload,
          })
          return {ok: true, response}
        } catch {
          toast.error('Failed to create location. Please try again.')
          return {ok: false, response: null}
        } finally {
          if (manageSaving) setSaving(false)
        }
      }
      if (!accountId) return {ok: true, response: null}
      if (manageSaving) setSaving(true)
      try {
        // Same rationale as the add-location branch above: prefer the
        // just-resolved values over `editValues` state in case this runs
        // before a setEditValues from the same validation pass has been
        // through a re-render.
        const values = resolvedValues || editValues
        const basePayload = formValuesToBillingPayload(values, account)
        const payload =
          type === 'child'
            ? // Send '' (not undefined) when cleared — the backend (intg-billing-client.js /
              // account-dao.js patchRecurlyAccount) treats `name` presence as authoritative for
              // both Company Name precedence and RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME, so an
              // intentional clear must actually reach it rather than being dropped as "unset".
              {...basePayload, name: values.locationName ?? ''}
            : basePayload
        await accountsApi.updateAccount(client, accountId, payload)
        return {ok: true, response: null}
      } catch {
        toast.error('Failed to save account. Please try again.')
        return {ok: false, response: null}
      } finally {
        if (manageSaving) setSaving(false)
      }
    },
    [client, editingTarget, editValues, selectedCountry],
  )

  // Shared by handleSaveInfo (fresh validation) and handleConfirmSuggestion
  // (accepting a Google recommendation — saves with it directly instead of
  // re-running validateAddress on values Google already resolved) so the
  // post-save toast/close/refresh logic lives in one place.
  const finishSaveInfo = useCallback(
    async ({values, timeZone}) => {
      const {ok, response} = await saveAccount({
        resolvedValues: values,
        timeZone,
      })
      if (!ok) return
      if (
        editingTarget?.type === 'add-location' &&
        response?.childProvisionFailed
      ) {
        toast.warning(
          'Location request submitted. Provisioning is in progress and may take a few minutes to appear.',
        )
      } else {
        const name =
          editingTarget?.type === 'add-location'
            ? 'Location added successfully.'
            : `${
                editValues.businessName || editingTarget?.account?.company || ''
              } updated successfully.`
        toast.success(name)
      }
      setEditingTarget(null)
      setEditValues({})
      handleSearch()
    },
    [saveAccount, editingTarget, editValues, handleSearch],
  )

  const handleSaveInfo = useCallback(async () => {
    proceedAfterSuggestionRef.current = false
    const addressResult = await runAddressValidation()

    if (!addressResult.success) return

    await finishSaveInfo({
      values: addressResult.values,
      timeZone: addressResult.timeZone,
    })
  }, [runAddressValidation, finishSaveInfo])

  // Everything that happens once every location's address has been validated
  // (single location, or every location in a multi-location purchase link) —
  // shared by handleSaveAndProceed's happy path and handleProceedFromReview
  // (reached after every flagged address is individually resolved in the
  // stack review UI), so the
  // upgradeTarget/legacy-subscription order-building logic below only exists
  // in one place.
  const proceedToOrder = useCallback(
    async ({
      account,
      type,
      parentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      parentAccountForOrder,
      addressResult,
    }) => {
      // For add-location: own the full-page spinner for POST + prefetch in one block
      // so saving never flips false mid-operation and the overlay never flashes off.
      if (type === 'add-location') {
        const emailForValidation =
          editValues.email ||
          parentAccount?.email ||
          parentAccount?.accountEmail ||
          null
        const capturedCountry =
          parentAccount?.country ||
          parentAccount?.address?.country ||
          selectedCountry ||
          'US'
        setSaving(true)
        try {
          const {ok, response} = await saveAccount({
            manageSaving: false,
            resolvedValues: addressResult?.values,
            timeZone: addressResult?.timeZone,
          })
          if (!ok) return
          if (response?.childProvisionFailed) {
            toast.warning(
              'Location request submitted. Provisioning is in progress — please try ordering once it appears in the list.',
            )
            setEditingTarget(null)
            setEditValues({})
            handleSearch()
            return
          }
          const thryvId = response?.thryvId || response?.ccId
          if (!thryvId) {
            toast.error('Unable to start a new order without a Thryv ID.')
            return
          }
          if (
            await hasBlockingPastDueInvoice(client, thryvId, capturedCountry)
          ) {
            setPastDueInvoiceContext({
              thryvId,
              country: capturedCountry,
              email: emailForValidation,
            })
            setShowPastDueInvoiceModal(true)
            return
          }
          // Land on the same Account Setup Catalog Selection step (step 3) that
          // "Create Purchase Link" uses for an existing location, instead of the
          // legacy standalone /order/new page — this is a brand-new location with
          // no subscriptions yet, so there's no upgradeTarget check to make first.
          const all5PlanPrices = await client('plans/5/v1/list').catch(
            () => null,
          )
          const newLocation = {
            thryvId,
            code: thryvId,
            company: editValues.businessName || '',
            email: emailForValidation,
            country: capturedCountry,
            billTo: editValues.billTo === 'self' ? 'self' : 'parent',
          }
          const purchaseLink = buildPurchaseLinkState({
            parentAccount,
            locationsOverride: [newLocation],
            accountSubscriptions: {},
            selectedLocation: newLocation,
            editValues,
          })
          setEditingTarget(null)
          setEditValues({})
          dispatch(
            setProductConfigInfo(
              applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
            ),
          )
          dispatch(setAccountInfo(purchaseLink.accountInfo))
          dispatch(
            setSalesAttributionFields(purchaseLink.salesAttributionFields),
          )
          dispatch(
            initLocations({
              locations: purchaseLink.locations,
              selectedLocationId: purchaseLink.selectedLocationId,
              orderMode: 'location',
            }),
          )
          dispatch(setStep(2))
          navigate('/accounts/account-setup', {
            state: {
              returnTo: '/accounts/all-accounts',
              returnSearch: searchQuery,
            },
          })
        } finally {
          setSaving(false)
        }
        return
      }

      setSaving(true)
      try {
        const {ok} = await saveAccount({
          manageSaving: false,
          resolvedValues: addressResult?.values,
          timeZone: addressResult?.timeZone,
        })
        if (!ok) return
        // Fold what was just saved back onto the records the purchase link is
        // seeded from — otherwise the catalog/order pages show the stale
        // account/locations fetched before the rep edited them.
        const savedValues = addressResult?.values || editValues
        const updatedAccount = applyValuesToAccount(account, savedValues)
        const updatedAccountId = resolveLocationAccountId(account)
        const locationsWithSaved = resolvedLocationsOverride?.map(loc =>
          resolveLocationAccountId(loc) === updatedAccountId
            ? updatedAccount
            : loc,
        )
        const thryvId =
          account?.thryvId || account?.thryvID || account?.code || account?.id
        if (!thryvId) {
          toast.error('Unable to start an upgrade without a Thryv ID.')
          return
        }
        const upgradeCountry =
          account?.country || account?.address?.country || selectedCountry
        if (await hasBlockingPastDueInvoice(client, thryvId, upgradeCountry)) {
          setPastDueInvoiceContext({
            thryvId,
            country: upgradeCountry,
            email: account?.email || account?.accountEmail,
          })
          setShowPastDueInvoiceModal(true)
          return
        }
        // See handleOrder: an account with a subscription id must still be
        // checked for v5; on failure keep the legacy flow.
        const legacySubscriptionId = getLegacySubscriptionId(account)
        const target = await client(
          `accounts/upgradeTarget?thryvId=${thryvId}`,
          {
            method: 'GET',
          },
        ).catch(err => {
          if (legacySubscriptionId) return null
          throw err
        })
        if (target?.v5upgrade) {
          const [accountSubscriptionsResponse, all5PlanPrices] =
            await Promise.all([
              client(`accounts/5/subscriptions?thryvId=${thryvId}`),
              client('plans/5/v1/list').catch(() => null),
            ])
          const accountSubscriptions = withAccountInfoXpCode(
            accountSubscriptionsResponse,
            editValues.xpCode || account?.xpCode,
            editValues.salesRepOrPartner || account?.salesRepOrPartner,
          )
          if (shouldUseLegacyPartnerOpsFlow(effectiveRoles)) {
            setEditingTarget(null)
            setEditValues({})
            navigate('/order/upgrade', {
              state: {
                order: {
                  thryvId,
                  v5upgrade: true,
                  billTo: accountSubscriptions?.accountInfo?.billTo,
                  CCAccountInfo: {thryvId},
                  accountSubscriptionData: accountSubscriptions,
                },
                navFrom: 'accounts',
                currentStep: 1,
                prefetchedPlanPrices: all5PlanPrices,
                prefetchedAccountSubscriptions: accountSubscriptions,
              },
            })
            return
          }
          const purchaseLink = buildPurchaseLinkState({
            parentAccount:
              type === 'parent'
                ? updatedAccount
                : parentAccountForOrder || parentAccount,
            locationsOverride: locationsWithSaved,
            accountSubscriptions,
            selectedLocation: updatedAccount,
            editValues,
            orderMode: savedOrderMode,
          })
          setEditingTarget(null)
          setEditValues({})
          dispatch(
            setProductConfigInfo(
              applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
            ),
          )
          dispatch(setAccountInfo(purchaseLink.accountInfo))
          dispatch(
            setSalesAttributionFields(purchaseLink.salesAttributionFields),
          )
          dispatch(
            initLocations({
              locations: purchaseLink.locations,
              selectedLocationId: purchaseLink.selectedLocationId,
              orderMode: savedOrderMode,
            }),
          )
          dispatch(setIsUpgrade(true))
          // accountSubscriptions was already fetched above for
          // purchaseLink.selectedLocationId — seed it into that location's slice
          // entry now so usePurchaseLinkLocation/usePurchaseLinkPreloadAllLocations
          // see isLoaded=true and skip re-fetching the exact same data.
          if (purchaseLink.selectedLocationId) {
            dispatch(
              setRecurlyAccountInfoForLocation({
                locationId: purchaseLink.selectedLocationId,
                recurlyAccountInfo: accountSubscriptions,
              }),
            )
          }
          dispatch(setStep(2))
          navigate('/accounts/account-setup', {
            state: {
              returnTo: '/accounts/all-accounts',
              returnSearch: searchQuery,
            },
          })
          return
        }
        if (legacySubscriptionId || target?.subscriptionId) {
          setEditingTarget(null)
          setEditValues({})
          navigateToUpgrade({
            ...buildLegacyUpgradeOrder(account, null, {
              subscriptionId: target?.subscriptionId,
            }),
            // On-behalf-of rep picked in the edit modal (no-op when none).
            ...buildBehalfRepUpgradeFields(savedValues),
          })
          return
        }
        toast.error('Unable to find a subscription for this account.')
      } catch (err) {
        toast.error(err?.message || 'Unable to start the upgrade order.')
      } finally {
        setSaving(false)
      }
    },
    [
      saveAccount,
      editValues,
      handleSearch,
      navigate,
      navigateToUpgrade,
      client,
      dispatch,
      selectedCountry,
      userFeatures,
      searchQuery,
    ],
  )

  // "Use recommended address" on the AddressSuggestionBanner — Google
  // already resolved this exact recommendation (lat/lng/timeZone) in the
  // validateAddress call that produced the suggestion, so this saves with
  // it directly rather than re-running address validation, which is what
  // could hit an ambiguous-locality case a second time and loop.
  //
  // If the rep originally clicked "Save & Proceed to Order" (tracked via
  // proceedAfterSuggestionRef, since the suggestion banner interrupts that
  // flow before it can reach proceedToOrder), resume straight into the order
  // instead of just saving and closing the modal.
  const handleConfirmSuggestion = useCallback(
    async updatedValues => {
      const timeZone = addressSuggestion?.timeZone
      setAddressSuggestion(null)
      if (proceedAfterSuggestionRef.current) {
        const {
          account,
          type,
          parentAccount,
          orderMode: savedOrderMode,
          locationsOverride,
          parentAccountForOrder,
        } = editingTarget || {}
        await proceedToOrder({
          account,
          type,
          parentAccount,
          savedOrderMode,
          resolvedLocationsOverride: locationsOverride,
          parentAccountForOrder,
          addressResult: {success: true, values: updatedValues, timeZone},
        })
        return
      }
      await finishSaveInfo({values: updatedValues, timeZone})
    },
    [addressSuggestion, finishSaveInfo, editingTarget, proceedToOrder],
  )

  const handleSaveAndProceed = useCallback(async () => {
    // GEP-6957: the account's country isn't editable in this modal, so a
    // restricted-country account reaching this button (opened via plain "Edit"
    // rather than "Create Purchase Link", which already blocks at handleOrder)
    // must still be stopped here with the same "contact Client Care" modal
    // handleOrder shows, rather than silently no-op'ing.
    if (
      isRestrictedAccountCreationCountry(
        getEditingTargetCountry(editingTarget, selectedCountry),
        Enable_International_Account_Creation,
      )
    ) {
      setRestrictedCountryModalOpen(true)
      return
    }

    proceedAfterSuggestionRef.current = true
    const addressResult = await runAddressValidation()

    const {
      account,
      type,
      parentAccount,
      orderMode: savedOrderMode,
      locationsOverride,
      parentAccountForOrder,
    } = editingTarget || {}

    const isMultiLocation =
      type !== 'add-location' &&
      savedOrderMode === 'parentPay' &&
      (locationsOverride?.length || 0) > 1

    // A genuine required-field problem on this step's own fields (sales-rep
    // attribution) always needs fixing here first, regardless of how many
    // locations this purchase link covers.
    if (
      !addressResult.success &&
      Object.keys(addressResult.fieldErrors || {}).length
    ) {
      return
    }
    // Single location: an unconfirmed suggestion just reveals this form
    // (there's nothing to build a stack out of).
    if (!isMultiLocation && !addressResult.success) return

    // Multi-location ("parentPay") purchase link — runAddressValidation above
    // only ever checked the single representative `account`. Check every
    // other location riding along in locationsOverride too; if any of them
    // (including the representative itself) need attention, drop into the
    // stack review UI instead of continuing straight to the order.
    let resolvedLocationsOverride = locationsOverride
    if (isMultiLocation) {
      const representativeLocationId = resolveLocationAccountId(account)
      const otherLocations = locationsOverride.filter(
        loc => resolveLocationAccountId(loc) !== representativeLocationId,
      )
      setSaving(true)
      let batchResults
      try {
        batchResults = await validateLocationsBatch(otherLocations)
      } finally {
        setSaving(false)
      }
      const representativeEntry = {
        locationId: representativeLocationId,
        account,
        values: addressResult.values || editValues,
        status: addressResult.success ? 'verified' : 'needs-attention',
        fieldErrors: {},
        suggestion: addressResult.suggestion || null,
      }
      const allEntries = [representativeEntry, ...batchResults]
      const firstFailing = allEntries.find(e => e.status === 'needs-attention')
      if (firstFailing) {
        setMultiLocationReview({
          locations: allEntries.map(e => e.account),
          statuses: Object.fromEntries(allEntries.map(e => [e.locationId, e])),
          valuesByLocation: Object.fromEntries(
            allEntries.map(e => [e.locationId, e.values]),
          ),
          activeLocationId: firstFailing.locationId,
        })
        return
      }
      resolvedLocationsOverride = allEntries.map(e => e.account)
    }

    await proceedToOrder({
      account,
      type,
      parentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      parentAccountForOrder,
      addressResult,
    })
  }, [
    runAddressValidation,
    validateLocationsBatch,
    proceedToOrder,
    editingTarget,
    editValues,
    Enable_International_Account_Creation,
    selectedCountry,
  ])

  // True once every location in the stack review has been individually
  // saved — gates whether the footer shows "Save" (per-location) or
  // "Proceed to Order" (see handleSaveReviewLocation/handleProceedFromReview).
  const allReviewLocationsResolved = !!(
    multiLocationReview &&
    Object.values(multiLocationReview.statuses).every(
      e => e.status === 'verified',
    )
  )

  // The location backing whichever tab is active in the stack review, and
  // whether its address has already been verified — drives whether the
  // detail panel shows the full edit form or just a read-only recap.
  const activeReviewLocation = multiLocationReview
    ? multiLocationReview.locations.find(
        loc =>
          resolveLocationAccountId(loc) ===
          multiLocationReview.activeLocationId,
      ) || editingTarget?.account
    : null

  const isActiveReviewLocationResolved =
    !!multiLocationReview &&
    multiLocationReview.statuses[multiLocationReview.activeLocationId]
      ?.status === 'verified'

  // Validates + saves just the currently-active location in the stack review
  // UI. Locations are fixed one at a time rather than all-at-once so the rep
  // can see each address resolve (or still need attention) immediately.
  // Shared by handleSaveReviewLocation's success path and
  // handleConfirmReviewSuggestion (accepting a Google recommendation without
  // re-validating it) — PATCHes the location and folds the result back into
  // multiLocationReview state so both callers stay in sync the same way.
  const persistReviewLocation = useCallback(
    async ({activeId, entry, finalValues}) => {
      // Fold the (possibly corrected) address back into the raw account shape
      // so downstream purchase-link building shows the address that was
      // actually validated/saved, not the stale fetched one.
      const updatedAccount = applyValuesToAccount(entry?.account, finalValues)
      // Mirrors saveAccount's own child-location payload exactly (see
      // above) — every location in this review is type 'child', so the
      // same locationName merge applies here.
      const basePayload = formValuesToBillingPayload(
        finalValues,
        updatedAccount,
      )
      await accountsApi.updateAccount(client, activeId, {
        ...basePayload,
        name: finalValues.locationName ?? '',
      })

      const updatedEntry = {
        ...entry,
        values: finalValues,
        status: 'verified',
        fieldErrors: {},
        suggestion: null,
        account: updatedAccount,
      }
      setMultiLocationReview(prev =>
        prev
          ? {
              ...prev,
              statuses: {...prev.statuses, [activeId]: updatedEntry},
              locations: prev.locations.map(loc =>
                resolveLocationAccountId(loc) === activeId
                  ? updatedAccount
                  : loc,
              ),
              valuesByLocation: {
                ...prev.valuesByLocation,
                [activeId]: finalValues,
              },
            }
          : prev,
      )
    },
    [client],
  )

  const handleSaveReviewLocation = useCallback(async () => {
    if (!multiLocationReview) return
    const activeId = multiLocationReview.activeLocationId
    const entry = multiLocationReview.statuses[activeId]
    const values = multiLocationReview.valuesByLocation[activeId]

    setSaving(true)
    try {
      const result = await validateAddress({
        ...values,
        country: entry?.account?.address?.country || entry?.account?.country,
      })
      const finalValues = result.values || values

      if (result.success) {
        await persistReviewLocation({activeId, entry, finalValues})
        toast.success('Location saved.')
      } else {
        const updatedEntry = {
          ...entry,
          values: finalValues,
          status: 'needs-attention',
          fieldErrors: result.fieldErrors || {},
          suggestion: result.suggestion || null,
        }
        setMultiLocationReview(prev =>
          prev
            ? {
                ...prev,
                statuses: {...prev.statuses, [activeId]: updatedEntry},
                valuesByLocation: {
                  ...prev.valuesByLocation,
                  [activeId]: finalValues,
                },
              }
            : prev,
        )
        toast.error('This address still needs attention.')
      }
    } catch {
      toast.error('Failed to save this location. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [multiLocationReview, validateAddress, persistReviewLocation])

  // "Use recommended address" inside the multi-location review's
  // AddressSuggestionBanner — same rationale as handleConfirmSuggestion:
  // Google already resolved this recommendation in the validateAddress call
  // that produced it, so save it directly instead of re-validating and
  // risking the same ambiguous-locality loop a second time.
  const handleConfirmReviewSuggestion = useCallback(
    async updatedValues => {
      if (!multiLocationReview) return
      const activeId = multiLocationReview.activeLocationId
      const entry = multiLocationReview.statuses[activeId]
      setSaving(true)
      try {
        await persistReviewLocation({
          activeId,
          entry,
          finalValues: updatedValues,
        })
        toast.success('Location saved.')
      } catch {
        toast.error('Failed to save this location. Please try again.')
      } finally {
        setSaving(false)
      }
    },
    [multiLocationReview, persistReviewLocation],
  )

  // Reached from the stack review UI once every location has been
  // individually saved (see handleSaveReviewLocation) — each location was
  // already validated + PATCHed as it was resolved, so this just hands off
  // to the same order-building flow used by the single-location happy path.
  const handleProceedFromReview = useCallback(async () => {
    if (!multiLocationReview) return
    // GEP-6957: a parentPay bundle can carry a location whose own country is
    // restricted even when the representative location (the one handleOrder's
    // entry check saw) isn't — this stack-review step never re-checked the
    // other bundled locations, so a restricted one could ride straight through
    // to an order. Block here the same way handleOrder does.
    const hasRestrictedLocation = multiLocationReview.locations.some(loc =>
      isRestrictedAccountCreationCountry(
        loc?.address?.country || loc?.country || loc?.countryCode,
        Enable_International_Account_Creation,
      ),
    )
    if (hasRestrictedLocation) {
      setRestrictedCountryModalOpen(true)
      return
    }
    const {
      account,
      type,
      parentAccount,
      orderMode: savedOrderMode,
      parentAccountForOrder,
    } = editingTarget || {}
    const resolvedLocationsOverride = multiLocationReview.locations
    // The representative location's latest (review-screen) values — without
    // these proceedToOrder would re-save it from stale first-modal editValues.
    const representativeValues =
      multiLocationReview.valuesByLocation[resolveLocationAccountId(account)]
    setMultiLocationReview(null)
    await proceedToOrder({
      account,
      type,
      parentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      parentAccountForOrder,
      addressResult: {values: representativeValues},
    })
  }, [
    multiLocationReview,
    editingTarget,
    proceedToOrder,
    Enable_International_Account_Creation,
  ])

  const handleEditClose = useCallback(() => {
    setEditingTarget(null)
    setEditValues({})
    setAddressErrors({})
    setAddressSuggestion(null)
    setRevealLocationDetails(false)
    setMultiLocationReview(null)
  }, [])

  const modalSubtitle =
    editingTarget?.type === 'add-location'
      ? editingTarget?.parentAccount?.company ||
        editingTarget?.parentAccount?.companyName
      : // The account-header "Create Purchase Link" flow opens this modal
      // scoped to a single representative location (see handleOrderForParent),
      // so editingTarget.account is just that one location — list every
      // parent-pay location this purchase link actually covers instead, so
      // the modal doesn't look like it's only asking about that one child.
      editingTarget?.locationsOverride
      ? editingTarget.locationsOverride
          .map(l => l?.name || l?.company || l?.companyName)
          .filter(Boolean)
          .join(', ')
      : editingTarget?.account?.name ||
        editingTarget?.account?.company ||
        editingTarget?.account?.companyName

  // "Create Purchase Link" (order) flow hides Save Information and always shows
  // Save & Proceed (even for a pre-5 parent, where it is normally hidden).
  const isOrderFlow = !!editingTarget?.fromOrder
  const showSaveInfoButton =
    editingTarget?.type !== 'add-location' && !isOrderFlow
  const showSaveAndProceedButton =
    editingTarget?.type !== 'parent' || isOrderFlow

  // GEP-6957: country isn't editable in this modal (AccountEditModal reads it
  // read-only off account/parentAccount — see its own `country` derivation),
  // so a restricted-country account can never be saved into a valid state here.
  // Disable both Save buttons rather than letting the rep save/proceed into an
  // order flow OOE can't actually support end to end for this country yet.
  const editCountry = getEditingTargetCountry(editingTarget, selectedCountry)
  const isEditCountryRestricted = isRestrictedAccountCreationCountry(
    editCountry,
    Enable_International_Account_Creation,
  )

  return (
    <>
      <ContentArea>
        {isEmbedded ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '4px 0',
              width: '100%',
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <Button variant="secondary-gray" size="sm" onClick={onBack}>
                &lt; Back
              </Button>
              {initialSearch && (
                <span style={{fontSize: '14px', color: '#555'}}>
                  Results for: <strong>{initialSearch}</strong>
                </span>
              )}
            </div>
            {isEmailOnlyChild && onAddAccount && (
              <Button
                variant="solid-gradiant"
                size="sm"
                onClick={() => onAddAccount(initialSearch)}
              >
                + Add Account
              </Button>
            )}
          </div>
        ) : (
          <Toolbar>
            <TabToggle tabs={accountTabs} />
            <SearchRow>
              <SearchWrapper>
                <Input
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  leftIcon={<StyledSearchIcon />}
                  rightIcon={searchQuery ? '×' : null}
                  onRightIconClick={searchQuery ? handleClearSearch : undefined}
                  placeholder="Search by email, Thryv ID, or EAID"
                  variant="form"
                  size="md"
                />
              </SearchWrapper>
              <Button
                variant="solid-dark-blue"
                size="sm"
                onClick={handleNewAccount}
                disabled={!isEmailOnlyChild && !isEmailNotFound}
              >
                <StyledAddIcon />
                <span>New Account</span>
              </Button>
            </SearchRow>
          </Toolbar>
        )}

        {loading && <Spinner />}
        {!loading && error && <StatusMessage isError>{error}</StatusMessage>}
        {!loading && !error && !hasSearched && (
          <StatusMessage>Search for an account to get started.</StatusMessage>
        )}
        {!loading && !error && hasSearched && accounts.length === 0 && (
          <StatusMessage>No accounts found.</StatusMessage>
        )}
        {!loading && !error && accounts.length > 0 && (
          <AccountList aria-label="All accounts">
            {accounts.map(account => (
              <AccountRow
                key={account.id}
                account={account}
                defaultOpen
                onViewDetails={() => setSelectedAccount(account)}
                onViewSubscription={loc => {
                  const locId = loc?._fromBillingService
                    ? loc.id
                    : loc?.code || loc?.id
                  setSelectedLoc({
                    ...loc,
                    prefetchedSubscriptions: subCache[locId] ?? null,
                  })
                }}
                onOrder={loc => handleOrder(loc, account)}
                onOrderForParent={handleOrderForParent}
                onEdit={() => handleEditParent(account)}
                onEditChild={loc => handleEditChild(loc)}
                onAddLocation={() => handleAddLocation(account)}
                showStatus
                orderingThryvId={orderingThryvId}
              />
            ))}
          </AccountList>
        )}
      </ContentArea>

      {/* Account details drawer */}
      <Drawer
        isOpen={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        direction="right"
        size="600px"
      >
        {selectedAccount && (
          <AccountDetailsDrawer
            account={selectedAccount}
            onClose={() => setSelectedAccount(null)}
            payBadgeStyle={payBadgeStyle}
          />
        )}
      </Drawer>

      {/* Subscription details drawer */}
      <Drawer
        isOpen={!!selectedLoc}
        onClose={() => setSelectedLoc(null)}
        direction="right"
        size="600px"
      >
        {selectedLoc && (
          <SubscriptionDetailsDrawer
            loc={selectedLoc}
            prefetchedData={selectedLoc?.prefetchedSubscriptions ?? null}
            onClose={() => setSelectedLoc(null)}
            source="all-accounts"
          />
        )}
      </Drawer>

      {/* Edit / Add location modal */}
      <Modal
        isOpen={!!editingTarget}
        onClose={saving || isValidatingAddress ? () => {} : handleEditClose}
        showCloseButton={!saving && !isValidatingAddress}
        closeOnBackdrop={false}
        closeOnEsc={!saving && !isValidatingAddress}
        title={
          multiLocationReview || (isOrderFlow && addressSuggestion)
            ? 'Manage Account'
            : isOrderFlow
            ? 'Is this sale on behalf of someone else?'
            : editingTarget?.type === 'add-location'
            ? 'Add New Location details'
            : editingTarget?.type === 'child'
            ? 'Edit Location details'
            : 'Edit Account details'
        }
        subtitle={multiLocationReview ? null : modalSubtitle}
        size="xl"
        width={multiLocationReview ? '1200px' : undefined}
        footerAlign="right"
        footer={
          multiLocationReview ? (
            allReviewLocationsResolved ? (
              <Button
                variant="solid-gradiant"
                onClick={handleProceedFromReview}
                disabled={saving || isValidatingAddress}
              >
                {saving ? 'Saving…' : 'Proceed to Order'}
              </Button>
            ) : (
              <Button
                variant="solid-gradiant"
                onClick={handleSaveReviewLocation}
                disabled={saving || isValidatingAddress}
              >
                {isValidatingAddress
                  ? 'Validating…'
                  : saving
                  ? 'Saving…'
                  : 'Save'}
              </Button>
            )
          ) : (
            <>
              {/* While a suggestion from "Save & Proceed to Order" is showing,
                  that button is relabelled "Save" — hide this one so the
                  footer doesn't show two near-identical save buttons. */}
              {showSaveInfoButton &&
                !(addressSuggestion && proceedAfterSuggestionRef.current) && (
                  <Button
                    variant="secondary-blue"
                    onClick={handleSaveInfo}
                    disabled={
                      saving ||
                      isValidatingAddress ||
                      editingTarget?.loading ||
                      isEditCountryRestricted
                    }
                  >
                    {isValidatingAddress
                      ? 'Validating…'
                      : saving
                      ? 'Saving…'
                      : 'Save Information'}
                  </Button>
                )}
              {showSaveAndProceedButton && (
                <Button
                  variant="solid-gradiant"
                  onClick={handleSaveAndProceed}
                  disabled={
                    saving || isValidatingAddress || editingTarget?.loading
                  }
                >
                  {isValidatingAddress
                    ? 'Validating…'
                    : saving
                    ? 'Saving…'
                    : addressSuggestion && proceedAfterSuggestionRef.current
                    ? 'Save'
                    : isOrderFlow
                    ? 'Continue'
                    : 'Save & Proceed to Order'}
                </Button>
              )}
            </>
          )
        }
      >
        {multiLocationReview ? (
          <SchedulingLayout style={{height: '65vh', overflow: 'hidden'}}>
            <LocationTabList style={{overflowY: 'auto', minHeight: 0}}>
              {multiLocationReview.locations.map((loc, index) => {
                const locId = resolveLocationAccountId(loc)
                const done =
                  multiLocationReview.statuses[locId]?.status === 'verified'
                return (
                  <LocationTabCard
                    key={locId}
                    as="button"
                    type="button"
                    active={locId === multiLocationReview.activeLocationId}
                    onClick={() => setActiveReviewLocation(locId)}
                  >
                    <LocationTabRow>
                      <LocationTabMainIcon>
                        <Icon
                          name="address"
                          size="sm"
                          color="#0327A9"
                          style={{width: 12, height: 12}}
                        />
                      </LocationTabMainIcon>
                      <LocationTabDetailsCol>
                        <LocationTabTopRow>
                          <LocationTabIndexLabel>
                            <Icon name="loc-ind" size="xxs" />
                            Location {index + 1}
                          </LocationTabIndexLabel>
                          <LocationTabBadge done={done}>
                            {done ? 'Resolved' : 'Not Resolved'}
                          </LocationTabBadge>
                        </LocationTabTopRow>
                        <LocationTabName>
                          {loc.locationName || loc.company || loc.companyName}
                        </LocationTabName>
                      </LocationTabDetailsCol>
                    </LocationTabRow>
                  </LocationTabCard>
                )
              })}
            </LocationTabList>
            <LocationDetailStack style={{overflowY: 'auto', minHeight: 0}}>
              {isActiveReviewLocationResolved ? (
                <ResolvedLocationSummary
                  name={
                    activeReviewLocation?.locationName ||
                    activeReviewLocation?.company ||
                    activeReviewLocation?.companyName
                  }
                  values={
                    multiLocationReview.valuesByLocation[
                      multiLocationReview.activeLocationId
                    ]
                  }
                />
              ) : (
                <AccountEditModal
                  key={multiLocationReview.activeLocationId}
                  roles={effectiveRoles}
                  account={activeReviewLocation}
                  type="child"
                  saving={saving}
                  isValidatingAddress={isValidatingAddress}
                  frontendOverrideSst={frontendOverrideSst}
                  hideTypeBadge
                  additionalHiddenFields={['salesRepOrPartner', 'billTo']}
                  valuesOverride={
                    multiLocationReview.valuesByLocation[
                      multiLocationReview.activeLocationId
                    ]
                  }
                  onValuesChange={values =>
                    updateReviewLocationValues(
                      multiLocationReview.activeLocationId,
                      values,
                    )
                  }
                  addressSuggestion={
                    multiLocationReview.statuses[
                      multiLocationReview.activeLocationId
                    ]?.suggestion
                  }
                  onApplySuggestion={handleConfirmReviewSuggestion}
                  isOrderFlow={!!editingTarget?.fromOrder}
                  isPre5={!!editingTarget?.isPre5}
                />
              )}
            </LocationDetailStack>
          </SchedulingLayout>
        ) : (
          editingTarget && (
            <AccountEditModal
              roles={effectiveRoles}
              account={editingTarget.account}
              type={editingTarget.type}
              parentAccount={editingTarget.parentAccount}
              onValuesChange={setEditValues}
              loading={editingTarget.loading}
              saving={saving}
              isValidatingAddress={isValidatingAddress}
              addressErrors={addressErrors}
              addressSuggestion={addressSuggestion}
              onApplySuggestion={handleConfirmSuggestion}
              frontendOverrideSst={frontendOverrideSst}
              hideLocationDetails={
                !!editingTarget?.fromOrder && !revealLocationDetails
              }
              // Single-location counterpart of the multi-location review's
              // AccountEditModal: while an address suggestion is being
              // resolved here, sales-rep attribution and Bill To aren't
              // relevant yet, same as the stack review UI.
              hideTypeBadge={!!addressSuggestion}
              additionalHiddenFields={
                addressSuggestion ? ['salesRepOrPartner', 'billTo'] : []
              }
              isOrderFlow={!!editingTarget?.fromOrder}
              isPre5={!!editingTarget?.isPre5}
              suggestionApplyLabel={
                proceedAfterSuggestionRef.current
                  ? 'Use recommended address & Proceed to Order'
                  : undefined
              }
            />
          )
        )}
      </Modal>

      {/* GEP-6957: shown instead of proceeding to the order flow when
          "Create Purchase Link" is clicked for a restricted-country account. */}
      <Modal
        isOpen={restrictedCountryModalOpen}
        onClose={() => setRestrictedCountryModalOpen(false)}
        title="Almost there!"
        size="sm"
        footer={
          <Button
            variant="solid-gradiant"
            onClick={() => setRestrictedCountryModalOpen(false)}
          >
            OK
          </Button>
        }
      >
        Account upgrades for this country aren&apos;t available in OOE just yet.
        For now, please contact Client Care for assistance updating this
        account.
      </Modal>

      {showPastDueInvoiceModal &&
        (SHOW_INVOICE_INFO_FOR_PAST_DUE ? (
          <PastDueInvoiceModal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            hideX
            onClose={() => setShowPastDueInvoiceModal(false)}
          >
            <InvoiceInformation
              thryvId={pastDueInvoiceContext?.thryvId}
              email={pastDueInvoiceContext?.email}
              selectedCountry={pastDueInvoiceContext?.country}
              setIsInvoiceBilled={() => setShowPastDueInvoiceModal(false)}
            />
          </PastDueInvoiceModal>
        ) : (
          <PastDueInvoiceModal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            footer
            action
            btnAction={() => setShowPastDueInvoiceModal(false)}
            onClose={() => setShowPastDueInvoiceModal(false)}
            btnActionText="Ok"
            hideX
          >
            <div style={{padding: '20px', textAlign: 'center'}}>
              <strong>This account has a past due invoice.</strong>
              <br></br>
              Please resolve the outstanding balance before processing a new
              order.
            </div>
          </PastDueInvoiceModal>
        ))}

      {!!orderingThryvId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.65)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner />
        </div>
      )}
    </>
  )
}
